import { useQuery } from '@tanstack/react-query'

import type { SlugAvailability } from '@/bindings/SlugAvailability'
import { slugAvailabilityQuery } from '@/features/restaurants/api'
import type { System } from '@/lib/auth-store'
import { type SlugProblem, slugProblem } from '@/lib/slug'
import { useDebounced } from '@/lib/use-debounced'

export interface SlugVerdict {
  problem: SlugProblem | undefined
  availability: SlugAvailability | 'checking' | undefined
  /** Safe to submit: well-formed, not reserved, and not known to be taken. */
  ok: boolean
}

const DEBOUNCE_MS = 300

/**
 * As-you-type slug verdict (URL scheme, 2026-09-01). Restaurant slugs ask the
 * availability endpoint (debounced, one indexed lookup) of `system`'s session;
 * branch slugs check offline against the restaurant's own branch list, which
 * the caller passes.
 */
export function useSlugVerdict(
  value: string,
  options: { remote?: boolean; system?: System; taken?: readonly string[] } = {},
): SlugVerdict {
  const problem = slugProblem(value)
  const debounced = useDebounced(value, DEBOUNCE_MS)
  const askable = Boolean(options.remote) && problem === undefined
  const query = useQuery({ ...slugAvailabilityQuery(debounced, options.system), enabled: askable && debounced === value })

  if (problem) return { problem, availability: undefined, ok: false }
  if (options.taken?.includes(value)) return { problem: undefined, availability: 'taken', ok: false }
  if (!askable) return { problem: undefined, availability: undefined, ok: true }
  // A failed probe is not a verdict: submitting lets the backend decide (SLUG_TAKEN is race-free there).
  if (debounced !== value || query.isPending) return { problem: undefined, availability: 'checking', ok: false }
  const availability = query.data?.status
  return { problem: undefined, availability, ok: availability !== 'taken' && availability !== 'reserved' }
}
